import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { parseVideoUrl } from "@/lib/videoEmbed";
import { Loader2, Clapperboard, ImagePlus, X, Film } from "lucide-react";
import { toast } from "sonner";

const GENRES = [
  { value: "nollywood", label: "Nollywood" },
  { value: "drama", label: "Drama" },
  { value: "comedy", label: "Comedy" },
  { value: "action", label: "Action" },
  { value: "romance", label: "Romance" },
  { value: "thriller", label: "Thriller" },
  { value: "epic", label: "Epic / Historical" },
  { value: "documentary", label: "Documentary" },
  { value: "kids", label: "Kids & Family" },
  { value: "music", label: "Music / Musical" },
  { value: "other", label: "Other" },
];

const PLATFORMS = [
  "YouTube", "Netflix", "Prime Video", "Showmax", "IROKOtv",
  "Apple TV", "Google Play", "Vimeo", "My website", "Other",
];
const ACCESS = [
  { value: "free", label: "Free" },
  { value: "rent", label: "Rent" },
  { value: "buy", label: "Buy" },
];

const MovieNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    synopsis: "",
    genre: "nollywood",
    country: user?.country || "Nigeria",
    year: "",
    language: "",
    director: "",
    cast: "",
    runtime_min: "",
    trailer_url: "",
  });
  const [posterUrl, setPosterUrl] = useState("");
  const [watchOptions, setWatchOptions] = useState([
    { platform: "YouTube", access: "free", url: "", note: "" },
  ]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const addOption = () =>
    setWatchOptions((prev) => [...prev, { platform: "Netflix", access: "rent", url: "", note: "" }]);
  const removeOption = (i) => setWatchOptions((prev) => prev.filter((_, idx) => idx !== i));
  const setOption = (i, key, val) =>
    setWatchOptions((prev) => prev.map((o, idx) => (idx === i ? { ...o, [key]: val } : o)));

  const onPoster = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = data?.url || data?.image_url || data?.file_url;
      if (!url) throw new Error("no url");
      setPosterUrl(url);
    } catch (err) {
      toast.error("Poster upload failed — try a smaller image");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const trailerInfo = parseVideoUrl(form.trailer_url);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in first");
      return;
    }
    if (form.synopsis.trim().length < 20) {
      toast.error("Add a fuller synopsis (20+ characters)");
      return;
    }
    if (!form.trailer_url.trim()) {
      toast.error("A trailer link is required (YouTube, Vimeo or an mp4 link)");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        synopsis: form.synopsis.trim(),
        genre: form.genre,
        country: form.country.trim(),
        year: form.year ? Number(form.year) : null,
        language: form.language.trim() || null,
        director: form.director.trim() || null,
        cast: form.cast.trim() || null,
        runtime_min: form.runtime_min ? Number(form.runtime_min) : null,
        poster_url: posterUrl || null,
        trailer_url: form.trailer_url.trim(),
        watch_options: watchOptions
          .filter((o) => o.url.trim())
          .map((o) => ({
            platform: o.platform,
            access: o.access,
            url: o.url.trim(),
            note: o.note.trim() || null,
          })),
      };
      const { data } = await api.post("/movies", payload);
      toast.success("Your movie is live 🎬");
      navigate(`/movies/${data.movie_id}`);
    } catch (err) {
      toast.error(String(err?.response?.data?.detail || "Could not publish — please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0B0A0F] flex items-center justify-center px-5" data-testid="movies-new-needs-auth">
        <div className="bg-white/[0.04] border border-white/10 rounded-3xl p-10 max-w-md text-center text-white">
          <Clapperboard className="w-12 h-12 mx-auto text-white/30 mb-4" />
          <h2 className="font-display text-2xl mb-2">Sign in to submit your movie</h2>
          <p className="text-sm text-white/60 mb-6">
            We need an AfriMarket account so audiences can find and follow your work.
          </p>
          <a
            href={`/login?next=${encodeURIComponent("/movies/new")}`}
            className="inline-block px-6 py-2.5 rounded-full bg-[#E4B851] text-[#0B0A0F] text-sm font-bold"
          >
            Sign in
          </a>
        </div>
      </div>
    );
  }

  const inputCls =
    "mt-1 w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#E4B851] focus:ring-1 focus:ring-[#E4B851] outline-none";
  const labelCls = "text-xs font-semibold uppercase tracking-wider text-white/60";

  return (
    <div className="min-h-screen bg-[#0B0A0F] text-white pb-24" data-testid="movies-new-page">
      <div className="max-w-2xl mx-auto px-5 py-10">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-semibold text-[#E4B851] mb-3">
          <Film className="w-3 h-3" /> Submit a film
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Premiere your movie.</h1>
        <p className="text-white/60 text-sm mt-2">
          Upload a poster, drop your trailer link, and add where audiences can watch,
          buy, stream or rent the full film. Full-film hosting is coming later.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-5">
          <div>
            <label className={labelCls}>Movie title</label>
            <input
              type="text"
              required
              minLength={2}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="movies-input-title"
              placeholder="e.g. Lagos Nights"
              className={inputCls}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Genre</label>
              <select
                value={form.genre}
                onChange={(e) => setForm({ ...form, genre: e.target.value })}
                data-testid="movies-input-genre"
                className={`${inputCls} bg-[#17121b]`}
              >
                {GENRES.map((g) => (
                  <option key={g.value} value={g.value}>{g.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Release year</label>
              <input
                type="number"
                min="1900"
                max="2100"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                data-testid="movies-input-year"
                placeholder="2026"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Country</label>
              <input
                type="text"
                required
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                data-testid="movies-input-country"
                placeholder="Nigeria"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Language</label>
              <input
                type="text"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
                data-testid="movies-input-language"
                placeholder="English / Yoruba"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Synopsis</label>
            <textarea
              required
              minLength={20}
              rows={5}
              value={form.synopsis}
              onChange={(e) => setForm({ ...form, synopsis: e.target.value })}
              data-testid="movies-input-synopsis"
              placeholder="What's the story? Keep it punchy — enough to make someone hit play."
              className={`${inputCls} resize-y`}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Director (optional)</label>
              <input
                type="text"
                value={form.director}
                onChange={(e) => setForm({ ...form, director: e.target.value })}
                data-testid="movies-input-director"
                placeholder="Kunle A."
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Runtime (min, optional)</label>
              <input
                type="number"
                min="1"
                value={form.runtime_min}
                onChange={(e) => setForm({ ...form, runtime_min: e.target.value })}
                data-testid="movies-input-runtime"
                placeholder="112"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Cast (optional, comma-separated)</label>
            <input
              type="text"
              value={form.cast}
              onChange={(e) => setForm({ ...form, cast: e.target.value })}
              data-testid="movies-input-cast"
              placeholder="Genevieve N., Ramsey N."
              className={inputCls}
            />
          </div>

          {/* Poster */}
          <div>
            <label className={labelCls}>Poster</label>
            <div className="mt-2 flex items-center gap-4">
              {posterUrl ? (
                <div className="relative w-24 aspect-[2/3] rounded-lg overflow-hidden border border-white/15">
                  <img src={posterUrl} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setPosterUrl("")}
                    data-testid="movies-poster-remove"
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/60 hover:bg-black/80"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <label className="w-24 aspect-[2/3] rounded-lg border-2 border-dashed border-white/20 flex items-center justify-center text-white/40 cursor-pointer hover:border-[#E4B851]">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={onPoster}
                    className="hidden"
                    data-testid="movies-poster-input"
                  />
                </label>
              )}
              <p className="text-xs text-white/40">Portrait poster works best (2:3).</p>
            </div>
          </div>

          {/* Trailer */}
          <div>
            <label className={labelCls}>Trailer link *</label>
            <input
              type="url"
              required
              value={form.trailer_url}
              onChange={(e) => setForm({ ...form, trailer_url: e.target.value })}
              data-testid="movies-input-trailer"
              placeholder="https://youtu.be/…  or  https://vimeo.com/…  or  an .mp4 link"
              className={inputCls}
            />
            {form.trailer_url && (
              <p className={`text-xs mt-1 ${trailerInfo.kind === "none" ? "text-[#F26D5B]" : "text-[#7BC47F]"}`}>
                {trailerInfo.kind === "none"
                  ? "We couldn't recognise that as a YouTube, Vimeo or video link — it may not play."
                  : `Looks good — ${trailerInfo.kind} trailer detected.`}
              </p>
            )}
          </div>

          {/* Where to watch — multiple platforms */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-wider text-[#E4B851] font-semibold">Where to watch the full film</div>
              <button
                type="button"
                onClick={addOption}
                data-testid="movies-add-watch-option"
                className="text-xs font-semibold text-[#E4B851] hover:underline"
              >
                + Add platform
              </button>
            </div>
            <p className="text-xs text-white/40 -mt-2">
              Add every place your film is available — pick the platform, whether it's free / rent / buy, and paste the link.
            </p>
            {watchOptions.map((o, i) => (
              <div key={i} className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2" data-testid={`movies-watch-option-${i}`}>
                <div className="flex gap-2">
                  <select
                    value={o.platform}
                    onChange={(e) => setOption(i, "platform", e.target.value)}
                    data-testid={`movies-watch-platform-${i}`}
                    className={`${inputCls} bg-[#17121b] flex-1`}
                  >
                    {PLATFORMS.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                  <select
                    value={o.access}
                    onChange={(e) => setOption(i, "access", e.target.value)}
                    data-testid={`movies-watch-access-${i}`}
                    className={`${inputCls} bg-[#17121b] w-28`}
                  >
                    {ACCESS.map((a) => (
                      <option key={a.value} value={a.value}>{a.label}</option>
                    ))}
                  </select>
                  {watchOptions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOption(i)}
                      data-testid={`movies-watch-remove-${i}`}
                      className="px-2 rounded-lg border border-white/15 text-white/50 hover:text-[#F26D5B] hover:border-[#F26D5B]/50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <input
                  type="url"
                  value={o.url}
                  onChange={(e) => setOption(i, "url", e.target.value)}
                  data-testid={`movies-watch-url-${i}`}
                  placeholder="https://… link to watch / rent / buy"
                  className={inputCls}
                />
                {o.access !== "free" && (
                  <input
                    type="text"
                    value={o.note}
                    onChange={(e) => setOption(i, "note", e.target.value)}
                    data-testid={`movies-watch-note-${i}`}
                    placeholder="Price (optional) — e.g. £3.99 rent, £9.99 buy"
                    className={inputCls}
                  />
                )}
              </div>
            ))}
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="movies-submit-btn"
            className="w-full py-3.5 rounded-full font-bold text-[#0B0A0F] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(120deg, #E4B851 0%, #F26D5B 100%)" }}
          >
            {submitting ? "Publishing…" : "Publish my movie"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MovieNew;
